import { useMemo, useState } from 'react';

import { Grid, Stack, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import useSWR from 'swr';

import { ActionButton, Card, FileCard, InputField, NotificationCard } from '~components';
import CustomDialog from '~components/dialogs/customDialog';
import VectorImage from '~components/media/vectorImage';
import { pagePathSegments } from '~constants';
import { StatusVariant } from '~enums';
import ApiError from '~services/apiError';
import { getService } from '~services/serviceFactory';
import { Dimens } from '~themes';
import { triggerBlobDownload } from '~utils/fileUtils';

import WebhooksService from '../services/webhooksService';

const webhooksService = getService(WebhooksService);

const WebhookDetailsContainer = () => {
	const { t } = useTranslation();
	const { palette } = useTheme();
	const navigate = useNavigate();

	const colSize = { xs: 12, md: 5 };
	const [hasSubmitted, setHasSubmitted] = useState(false);
	const [isUpdatingWebhook, setIsUpdatingWebhook] = useState(false);
	const [showWebhookResultDialog, setShowWebhookResultDialog] = useState(false);
	const [updateError, setUpdateError] = useState<ApiError | undefined>(undefined);

	const { data: certificate } = useSWR(
		webhooksService.getSkopeiConnectPublicKeyCertificateCacheKey(),
		() => {
			return webhooksService.getSkopeiConnectPublicKeyCertificateHead();
		},
		{
			revalidateOnMount: true,
			revalidateIfStale: true,
			revalidateOnFocus: true,
			revalidateOnReconnect: true,
		}
	);

	const { data, isLoading, isValidating } = useSWR(
		webhooksService.getRentalWebhookCacheKey(),
		([_path, _endpoint]: string[]) => {
			return webhooksService.getRentalWebhook();
		},
		{
			revalidateOnFocus: false,
			revalidateOnReconnect: false,
			revalidateOnMount: true,
		}
	);

	const [clientId, setClientId] = useState(data?.clientId ?? '');
	const [scope, setScope] = useState(data?.scope ?? '');
	const [tokenEndpoint, setTokenEndpoint] = useState(
		data?.tokenEndpoint?.replace('https://', '') ?? ''
	);
	const [webhookEndpoint, setWebhookEndpoint] = useState(
		data?.webhookEndpoint?.replace('https://', '') ?? ''
	);

	useMemo(() => {
		setClientId(data?.clientId ?? '');
		setScope(data?.scope ?? '');
		setTokenEndpoint(data?.tokenEndpoint?.replace('https://', '') ?? '');
		setWebhookEndpoint(data?.webhookEndpoint?.replace('https://', '') ?? '');
	}, [data]);

	const updateWebhook = () => {
		setHasSubmitted(true);
		if (
			clientId.length == 0 ||
			scope.length == 0 ||
			tokenEndpoint.length == 0 ||
			webhookEndpoint.length == 0
		) {
			return;
		}

		setIsUpdatingWebhook(true);
		webhooksService
			.updateRentalWebhook({
				clientId: clientId,
				scope: scope,
				tokenEndpoint: 'https://' + tokenEndpoint,
				webhookEndpoint: 'https://' + webhookEndpoint,
			})
			.then(() => {
				setUpdateError(undefined);
			})
			.catch((error: ApiError) => {
				setUpdateError(error);
			})
			.finally(() => {
				setShowWebhookResultDialog(true);
				setIsUpdatingWebhook(false);
			});
	};

	const downloadCertificate = async () => {
		const certificate = await webhooksService.getSkopeiConnectPublicKeyCertificate();
		console.log(certificate);
		if (certificate.file) {
			triggerBlobDownload(certificate.file, certificate.fileName);
		}
	};

	const wasSuccess = updateError === undefined;
	const resultSubmitLabel = t(wasSuccess ? 'continue' : 'change', { ns: 'actions' });
	const resultIcon =
		wasSuccess ?
			<VectorImage name='icon/check' height='32' width='32' />
		:	<VectorImage name='icon/cross' height='32' width='32' />;
	const resultTitle = t(
		wasSuccess ? 'webhook.labels.successfullyIntegrated' : 'webhook.labels.integrationFailed',
		{ ns: 'entities' }
	);

	return (
		<Stack gap={Dimens.spacing4xl}>
			{certificate && (
				<NotificationCard
					title={t('webhook.labels.certificateIsDueIn', {
						ns: 'entities',
						value: certificate?.expiryDate.fromNow() ?? '',
					})}
					statusVariant={StatusVariant.Warning}
				/>
			)}
			<Card loading={isLoading || isValidating}>
				<Stack gap={Dimens.spacing4xl}>
					<Grid container columnSpacing={Dimens.spacing9xl} width={'100%'}>
						<Grid size={colSize}>
							<Stack gap={Dimens.spacingXl}>
								<Typography variant='labelLg' color={palette.textPrimary.main}>
									{t('webhook.labels.identityProviderConfiguration', { ns: 'entities' })}
								</Typography>
								<InputField
									label={t('webhook.labels.clientId', { ns: 'entities' })}
									required
									hasError={hasSubmitted && clientId.length == 0}
									value={clientId}
									onChange={setClientId}
								/>
								<InputField
									label={t('webhook.labels.scope', { ns: 'entities' })}
									required
									value={scope}
									hasError={hasSubmitted && scope.length == 0}
									onChange={setScope}
								/>
								<InputField
									label={t('webhook.labels.tokenEndpoint', { ns: 'entities' })}
									required
									value={tokenEndpoint}
									hasError={hasSubmitted && tokenEndpoint.length == 0}
									prefix='https://'
									onChange={setTokenEndpoint}
								/>
							</Stack>
						</Grid>
						<Grid size={colSize}>
							<Stack gap={Dimens.spacingXl}>
								<Typography variant='labelLg' color={palette.textPrimary.main}>
									{t('webhook.labels.webhookConfiguration', { ns: 'entities' })}
								</Typography>
								<InputField
									label={t('webhook.labels.webhookEndpoint', { ns: 'entities' })}
									value={webhookEndpoint}
									prefix='https://'
									required
									hasError={hasSubmitted && webhookEndpoint.length == 0}
									onChange={setWebhookEndpoint}
								/>
							</Stack>
						</Grid>
					</Grid>
					<Grid container columnSpacing={Dimens.spacing9xl} width={'100%'}>
						<Grid size={12}>
							<Typography variant='labelLg' color={palette.textPrimary.main}>
								{t('webhook.labels.certificate', { ns: 'entities' })}
							</Typography>
						</Grid>
						<Grid size={colSize}>
							<Stack gap={Dimens.spacingXl}>
								<InputField
									label={t('webhook.labels.thumbprint', { ns: 'entities' })}
									readonly
									disabled
									copyable
									value={certificate?.thumbprint ?? ''}
									onChange={() => {}}
								/>
								<InputField
									label={t('webhook.labels.rotationDate', { ns: 'entities' })}
									readonly
									disabled
									value={certificate?.expiryDate.toString()}
									onChange={() => {}}
								/>
							</Stack>
						</Grid>
						<Grid size={colSize}>
							{certificate && (
								<FileCard
									fileName={certificate.fileName}
									fileSize={certificate.readableFileSize}
									fileType='pem'
									label={t('webhook.labels.downloadCertificate', { ns: 'entities' })}
									onDownloadClicked={downloadCertificate}
								/>
							)}
						</Grid>
					</Grid>
					<ActionButton
						sx={{ alignSelf: 'end', width: Dimens.spacing12xl }}
						variant={StatusVariant.Brand}
						label={t('save', { ns: 'actions' })}
						loading={isUpdatingWebhook}
						onClick={updateWebhook}
					/>
				</Stack>
			</Card>

			{/* TODO: Can be it's own component, so it doesn't need to be repeated in createWebhookContainer and can be tested more easily. */}
			<CustomDialog
				open={showWebhookResultDialog}
				title={resultTitle}
				icon={resultIcon}
				confirmButtonText={resultSubmitLabel}
				cancellable={false}
				variant={wasSuccess ? StatusVariant.Brand : StatusVariant.Destructive}
				onConfirm={() => {
					if (!wasSuccess) setShowWebhookResultDialog(false);
					else navigate('/' + pagePathSegments.Webhooks);
				}}
				onClose={() => setShowWebhookResultDialog(false)}
				onDecline={() => setShowWebhookResultDialog(false)}
				sx={{ p: Dimens.spacingMd }}
			>
				{updateError?.errorCode === 'validationError' ?
					<NotificationCard
						title={updateError?.getDescription()}
						statusVariant={StatusVariant.Destructive}
					/>
				:	<Stack gap={Dimens.spacing3xl}>
						{updateError?.errorCode === 'WHK_001' ?
							<NotificationCard
								title={t('webhook.labels.configurationCorrect', { ns: 'entities' })}
								caption={updateError?.detail}
								statusVariant={StatusVariant.Destructive}
							/>
						:	<NotificationCard
								title={t('webhook.labels.configurationCorrect', { ns: 'entities' })}
								statusVariant={StatusVariant.Success}
							/>
						}
						{updateError?.errorCode === 'WHK_002' ?
							<NotificationCard
								title={t('webhook.labels.omitAuthorization', { ns: 'entities' })}
								caption={updateError?.detail}
								statusVariant={StatusVariant.Destructive}
							/>
						:	<NotificationCard
								title={t('webhook.labels.omitAuthorization', { ns: 'entities' })}
								statusVariant={StatusVariant.Success}
							/>
						}
						{updateError?.errorCode === 'WHK_003' ?
							<NotificationCard
								title={t('webhook.labels.errorCouldNotBeProcessed', { ns: 'entities' })}
								caption={updateError?.detail}
								statusVariant={StatusVariant.Destructive}
							/>
						:	<NotificationCard
								title={t('webhook.labels.errorProcessed', { ns: 'entities' })}
								statusVariant={StatusVariant.Success}
							/>
						}
					</Stack>
				}
			</CustomDialog>
		</Stack>
	);
};

export default WebhookDetailsContainer;
